import { useState } from 'react';
import { ErrorBanner, PageHead } from '../components/ui';
import { formatCompactCurrency, num, pct } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { AdminSummary, Me } from '../lib/types';
import { Tabs } from './am/amUi';
import { AdminCalls } from './admin/AdminCalls';
import { AdminContracts } from './admin/AdminContracts';
import { AdminDepot } from './admin/AdminDepot';
import { AdminOverview } from './admin/AdminOverview';
import { AdminPeople } from './admin/AdminPeople';
import { AdminProcurement } from './admin/AdminProcurement';
import { AdminRegistrations } from './admin/AdminRegistrations';
import { AdminScorecard } from './admin/AdminScorecard';
import { AdminSops } from './admin/AdminSops';
import { AdminVendors } from './admin/AdminVendors';
import { AdminSalesOrders } from './admin/AdminSalesOrders';
import type { AdminTab } from './admin/adminTypes';

/**
 * Admin — the operations admin dashboard, moved in from `C:\Development\admin`
 * (deployed as admin-toolbox-jcurve). This is what the Admin card opens.
 *
 * Ten registers, and the analytics computed live from them: inbound call
 * performance, procurement and receiving, contract renewals, depot stock, the
 * SOP register, vendor relationships, business registrations and insurance,
 * internal comms, and the KPI scorecard those functions are measured against.
 *
 * WHAT CAME ACROSS AND WHAT DID NOT
 *
 * The schema and every screen came across. The DATA did not, and that is the one
 * thing to know before opening this: `npm run seed` in that app wiped and
 * regenerated its whole dataset — 30 staff, 180 days of call and procurement
 * history, 28 contracts, 84 depot assets, 32 SOPs, 18 registrations and 30 KPIs
 * with a year of results — from a fixed-seed PRNG anchored to today. None of it
 * was real, and its clients, vendors and staff matched nothing in this directory.
 *
 * So these registers start EMPTY, and every tab says so in its own words rather
 * than drawing a chart of nothing. That is the opposite call to the one migration
 * 047 made about the NOC dashboards, and the difference is the whole test: those
 * were dropped because a generator was their only possible source, while these
 * are registers somebody fills in. An empty contract book becomes useful the
 * first time a real contract is typed into it; a contract book of invented
 * renewals never does.
 *
 * Its second staff list did not come across either. Birthdays, anniversaries,
 * tenure and headcount read `core.Employee` — the directory this app owns — and
 * date of birth was added to `coo.EmployeeProfile`, which already holds the
 * personal half of a person's record. See migration 048.
 */

const TABS: { key: AdminTab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'scorecard', label: 'Scorecard' },
  { key: 'contracts', label: 'Contracts' },
  { key: 'procurement', label: 'Procurement' },
  { key: 'salesOrders', label: 'Sales orders' },
  { key: 'depot', label: 'Depot' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'sops', label: 'SOPs' },
  { key: 'registrations', label: 'Registrations' },
  { key: 'calls', label: 'Inbound calls' },
  { key: 'people', label: 'People & comms' },
];

export function AdminPage() {
  const summary = useResource<AdminSummary>('/admin/summary');
  const me = useResource<Me>('/me');
  const [tab, setTab] = useState<AdminTab>('overview');

  /* Editor or better. The API enforces this on every mutation regardless — see
     requireWrite in routes/admin.ts — so this only decides whether a button a
     viewer cannot use is shown to them at all. */
  const canWrite = me.data ? me.data.accessLevel !== 'viewer' : false;

  /* Every tab reloads the summary after a write, so the band above the tabs and
     the tab below it can never disagree about a number they both show. */
  const onChanged = summary.reload;

  return (
    <>
      <PageHead
        title="Admin"
        lede="What the admin function owns: the contract book and its renewal deadlines, procurement and receiving, depot stock, the SOP register, suppliers, the paperwork that has to stay in date, and the measures all of it is judged on."
      />

      {summary.error && <ErrorBanner message={summary.error} />}
      {summary.data && <AdminHero summary={summary.data} />}

      <Tabs<AdminTab> tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'overview' && <AdminOverview onNavigate={setTab} />}
      {tab === 'scorecard' && <AdminScorecard canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'contracts' && <AdminContracts canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'procurement' && <AdminProcurement canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'salesOrders' && <AdminSalesOrders />}
      {tab === 'depot' && <AdminDepot canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'vendors' && <AdminVendors canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'sops' && <AdminSops canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'registrations' && <AdminRegistrations canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'calls' && <AdminCalls canWrite={canWrite} onChanged={onChanged} />}
      {tab === 'people' && <AdminPeople canWrite={canWrite} onChanged={onChanged} />}
    </>
  );
}

/**
 * The four figures worth carrying above every tab.
 *
 * Chosen for what somebody has to DO about them, not for size. Client MRR is the
 * exception and leads because it is the number this register exists to protect —
 * but the three beside it are all counts of work: a notice deadline closing, a
 * lapsed policy, a receipt that has not arrived.
 *
 * Each shows its gap rather than its total. "34 SOPs" is inventory; "6 overdue
 * for review" is the thing to act on.
 */
function AdminHero({ summary: s }: { summary: AdminSummary }) {
  const attention =
    s.registrations.lapsed + s.contracts.noticeDeadline30 + s.procurement.overdueReceipts;

  return (
    <div className="grid grid-4 mb-4">
      <div className="stat">
        <div className="label">Client MRR</div>
        <div className="figure">{formatCompactCurrency(s.contracts.clientMrr)}</div>
        <div className="foot">
          {s.contracts.total === 0
            ? 'no contracts recorded yet'
            : `across ${num(s.contracts.total)} ${s.contracts.total === 1 ? 'contract' : 'contracts'}`}
        </div>
      </div>

      <div className="stat">
        <div className="label">Notice deadlines</div>
        <div className={`figure${s.contracts.noticeDeadline30 > 0 ? ' accent' : ''}`}>
          {s.contracts.noticeDeadline30}
        </div>
        <div className="foot">
          {s.contracts.expiring90 > 0
            ? `within 30 days · ${s.contracts.expiring90} expiring in 90`
            : 'within the next 30 days'}
        </div>
      </div>

      <div className="stat">
        <div className="label">Paperwork lapsed</div>
        <div className={`figure${s.registrations.lapsed > 0 ? ' accent' : ''}`}>
          {s.registrations.lapsed}
        </div>
        <div className="foot">
          {s.registrations.total === 0
            ? 'nothing on the register yet'
            : `of ${num(s.registrations.total)} · ${s.registrations.dueIn30} due in 30 days`}
        </div>
      </div>

      <div className="stat">
        <div className="label">Scorecard health</div>
        <div className="figure">
          {s.scorecard.weightedHealth === null ? '—' : pct(s.scorecard.weightedHealth, 0)}
        </div>
        <div className="foot">
          {/* A weighted health figure with nothing behind it would read as a
              measured zero, so the foot says which it is. */}
          {s.scorecard.measures === 0
            ? 'no measures defined yet'
            : s.scorecard.weightedHealth === null
              ? `${s.scorecard.period} · nothing recorded`
              : `${s.scorecard.period} · ${s.scorecard.offTarget} off target`}
        </div>
      </div>

      {/* Not a fifth tile: a one-line summary of the three counts above, so the
          band reads as a sentence rather than as four unrelated numbers. */}
      {attention === 0 && s.contracts.total + s.registrations.total > 0 && (
        <p className="sub admin-hero-note">Nothing is overdue across the registers.</p>
      )}
    </div>
  );
}
